import { Injectable, InjectionToken, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { saveAs } from 'file-saver';
import { EMPTY, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap, timeout } from 'rxjs/operators';
import { BackendDownloadRequest } from '../types';
import {
  buildBackendApiUrl,
  buildBackendApiUrlWithParams,
  createBackendRequestOptionsWithExtras,
} from './backend-request-helpers';
import { DeviceService } from './device.service';
import { SessionService } from './session.service';

const NATIVE_EXPORT_CACHE_DIR = 'shared';

type NativeTransactionExport = {
  mkdir: typeof Filesystem.mkdir;
  downloadFile: typeof Filesystem.downloadFile;
  share: typeof Share.share;
};

// Wraps the Capacitor plugin calls behind an injectable boundary so specs can mock native
// filesystem/share behavior without touching the real plugin registry.
export const NATIVE_TRANSACTION_EXPORT = new InjectionToken<NativeTransactionExport>('Native transaction export', {
  providedIn: 'root',
  factory: () => ({
    mkdir: options => Filesystem.mkdir(options),
    downloadFile: options => Filesystem.downloadFile(options),
    share: options => Share.share(options),
  }),
});

@Injectable({ providedIn: 'root' })
export class TransactionExportService {
  private http = inject(HttpClient);
  private device = inject(DeviceService);
  private sessionService = inject(SessionService);
  private nativeExport = inject<NativeTransactionExport>(NATIVE_TRANSACTION_EXPORT);

  downloadTransactions$(requestData: BackendDownloadRequest): Observable<Blob> {
    if (this.device.isAndroidApp || this.device.isIosApp) return this.downloadNative$(requestData);

    const { type, apiRoute, data = {}, params = null, authed = true } = requestData;
    const options = createBackendRequestOptionsWithExtras({
      params,
      authed,
      extras: {
        responseType: 'blob' as const,
      },
    });

    const apiUrl = buildBackendApiUrl(apiRoute);
    const requestByType: Record<BackendDownloadRequest['type'], () => Observable<Blob>> = {
      find: () => this.http.get(apiUrl, options),
      get: () => this.http.get(apiUrl, options),
      create: () => this.http.post(apiUrl, data, options),
    };

    return requestByType[type]().pipe(
      tap(response => saveAs(response, 'budgets-transactions')),
      timeout(5000),
    );
  }

  private downloadNative$(requestData: BackendDownloadRequest): Observable<Blob> {
    // Native export only ever serves read requests (LCL-129); the legacy Filesystem.downloadFile
    // implementation ignores `data` on Android, so this path stays GET-only rather than honoring
    // BackendDownloadRequest's create/POST variant.
    const { apiRoute, params = null, authed = true } = requestData;

    return (authed ? this.sessionService.getOneSession$ : of(null)).pipe(
      take(1),
      switchMap(session => {
        if (authed && !session) return EMPTY;

        const apiUrl = buildBackendApiUrlWithParams(apiRoute, params);
        const filePath = `${NATIVE_EXPORT_CACHE_DIR}/budgets-transactions-${new Date().getTime()}.csv`;

        // Filesystem.downloadFile's legacy Android implementation ignores `recursive` and never
        // creates the destination's parent directory, so the cache subdirectory must exist first.
        return from(
          this.nativeExport.mkdir({
            directory: Directory.Cache,
            path: NATIVE_EXPORT_CACHE_DIR,
            recursive: true,
          }),
        ).pipe(
          catchError(() => of(undefined)),
          switchMap(() =>
            from(
              this.nativeExport.downloadFile({
                url: apiUrl,
                method: 'GET',
                path: filePath,
                directory: Directory.Cache,
                recursive: true,
                ...(session?.jwt ? { headers: { Authorization: `Bearer ${session.jwt}` } } : {}),
              }),
            ),
          ),
          switchMap(result => {
            if (!result.path) throw new Error('The transaction CSV download did not return a file path');

            const fileUrl = result.path.startsWith('file://') ? result.path : `file://${result.path}`;
            return from(
              this.nativeExport.share({
                title: 'Budgets transactions',
                files: [fileUrl],
                dialogTitle: 'Save transactions CSV',
              }),
            );
          }),
          map(() => new Blob()),
        );
      }),
    );
  }
}
